import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Info } from 'lucide-react';

interface TransactionImportValueTabProps {
  readonly useGrossValue: boolean;
  readonly onUseGrossValueChange: (useGross: boolean) => void;
}

export function TransactionImportValueTab({
  useGrossValue,
  onUseGrossValueChange,
}: Readonly<TransactionImportValueTabProps>) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Asset Value Calculation
        </Label>
        <p className="text-xs text-slate-600 dark:text-slate-400">
          Choose whether to use net or gross transaction values when creating assets
        </p>
      </div>

      <RadioGroup
        value={useGrossValue ? 'gross' : 'net'}
        onValueChange={(value) => onUseGrossValueChange(value === 'gross')}
      >
        <div className="space-y-3">
          {/* Net Value Option */}
          <div className="flex items-start space-x-3 p-4 rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer">
            <RadioGroupItem value="net" id="net" className="mt-0.5" />
            <div className="flex-1">
              <label
                htmlFor="net"
                className="text-sm font-medium text-slate-900 dark:text-slate-100 cursor-pointer block mb-1"
              >
                Net Value (Excluding VAT)
              </label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Use the transaction value excluding VAT as the asset's bought value. This is the standard approach for most asset registers.
              </p>
            </div>
          </div>

          {/* Gross Value Option */}
          <div className="flex items-start space-x-3 p-4 rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer">
            <RadioGroupItem value="gross" id="gross" className="mt-0.5" />
            <div className="flex-1">
              <label
                htmlFor="gross"
                className="text-sm font-medium text-slate-900 dark:text-slate-100 cursor-pointer block mb-1"
              >
                Gross Value (Including VAT)
              </label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Use the transaction value including VAT as the asset's bought value. Use this if your organisation cannot reclaim VAT.
              </p>
            </div>
          </div>
        </div>
      </RadioGroup>

      {/* Information Box */}
      <div className="flex gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="space-y-2 text-xs">
          <p className="text-blue-900 dark:text-blue-100 font-medium">
            About Asset Valuation
          </p>
          <p className="text-blue-800 dark:text-blue-200">
            The value you select will be used as the "Bought Value" for each asset created from the selected transactions. This value will be used for depreciation calculations.
          </p>
          <p className="text-blue-800 dark:text-blue-200">
            Most organizations use <strong>Net Value</strong> as it represents the actual cost of the asset before tax.
          </p>
        </div>
      </div>

      {/* Current Selection Summary */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 block">
          Current Selection
        </Label>
        <div className="text-sm text-slate-700 dark:text-slate-300">
          Assets will be created using <strong>{useGrossValue ? 'Gross Value (Inc. VAT)' : 'Net Value (Ex. VAT)'}</strong>
        </div>
      </div>
    </div>
  );
}
